// One row in the friends-list section of the Friends tab.
// Phase 17 Plan 06 Task 1 (cleanup-reskin) — tokens + Btn primitive.

import type { components } from '@runegambit/api-types'
import { type JSX, useCallback, useState } from 'react'
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl'

import { ApiError } from '../../net/client.js'
import { removeFriend } from '../../net/friends.js'
import { useRouter } from '../../router/router.js'
import { refreshFriendsList } from '../../stores/friendsList.js'
import { Avatar } from '../components/Avatar'
import { Btn } from '../components/Btn'

export type FriendRowEntry = components['schemas']['Friend'] & {
  /** Optional forward-compat field — currently never present on the wire. */
  readonly last_seen_at?: number
}

export interface FriendRowProps {
  readonly friend: FriendRowEntry
}

export function FriendRow({ friend }: FriendRowProps): JSX.Element {
  const intl = useIntl()
  const router = useRouter()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const handleActivate = useCallback((): void => {
    router.navigate(`/profile/${friend.user_uuid}`)
  }, [router, friend.user_uuid])

  const handleRemove = useCallback(async () => {
    if (busy) return
    setBusy(true)
    try {
      await removeFriend(friend.user_uuid)
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 404) {
        setBusy(false)
        setConfirmOpen(false)
        return
      }
    }
    void refreshFriendsList()
  }, [busy, friend.user_uuid])

  const initial = friend.display_name.slice(0, 1).toUpperCase()
  const lastSeenAt = friend.last_seen_at

  return (
    <div className="row" data-friend-row="true">
      <button
        type="button"
        data-testid={`friend-row-${friend.user_uuid}`}
        onClick={handleActivate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flex: '1 1 auto',
          minWidth: 0,
          padding: 0,
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--color-text-primary)',
        }}
      >
        <Avatar initial={initial} dataTestid="friend-row-avatar" />
        <span
          style={{
            flex: '1 1 auto',
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-16)',
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {friend.display_name}
        </span>
        {lastSeenAt !== undefined && (
          <span
            data-testid="friend-row-last-seen"
            style={{
              flex: '0 0 auto',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--color-surface-deep)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-12)',
            }}
          >
            <FormattedMessage
              id="social.friends.last_seen"
              defaultMessage="Last seen {minutes} min ago"
              values={{
                minutes: (
                  <FormattedRelativeTime
                    value={Math.round((lastSeenAt - Date.now()) / 60000)}
                    unit="minute"
                    numeric="always"
                  />
                ),
              }}
            />
          </span>
        )}
      </button>
      {confirmOpen ? (
        <div
          data-testid={`friend-row-confirm-${friend.user_uuid}`}
          style={{
            display: 'flex',
            gap: 'var(--space-1)',
            flex: '0 0 auto',
          }}
        >
          <Btn
            kind="ghost"
            size="sm"
            onClick={() => setConfirmOpen(false)}
            disabled={busy}
          >
            <FormattedMessage
              id="social.friends.remove.cancel_cta"
              defaultMessage="Cancel"
            />
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            onClick={() => {
              void handleRemove()
            }}
            disabled={busy}
          >
            <FormattedMessage
              id="social.friends.remove.confirm_cta"
              defaultMessage="Remove"
            />
          </Btn>
        </div>
      ) : (
        <Btn
          kind="ghost"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          ariaLabel={intl.formatMessage({
            id: 'social.friends.remove_friend_aria',
          })}
        >
          {'⋯'}
        </Btn>
      )}
    </div>
  )
}
